<template>
  <div v-if="init" class="amap-building-layer-tool">
    <Teleport to="#amap-control">
      <div :class="{ 'building-control': true, 'building-active': showBuilding }">
        <el-tooltip content="建筑图层" effect="dark" placement="left">
          <img src="@p-v/packages/Map/AMapChart/assets/building.png" alt="建筑图层" class="building-icon" @click="toggleBuilding" />
        </el-tooltip>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, inject, watch, computed, onMounted } from "vue"
import { ElTooltip } from "element-plus"

defineOptions({
  name: "AMapBuildingLayer",
})

const init = ref<boolean>(false)

const props = defineProps({
  // 是否初始化完成
  show: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(["building-toggle"])

// 从父组件注入地图实例
const amap = inject<any>("amap")
const mapFeatures = inject<any>("mapFeatures")

const showBuilding = computed(() => {
  // 如果地图特性中包含"building"，则表示建筑图层已显示
  return mapFeatures.value.includes("building")
})

// 监听地图实例变化
watch(
  () => amap.value,
  map => {
    if (map) {
      // 初始化建筑图层工具
      console.log("初始化建筑图层工具", map)
      init.value = true
    }
  },
  { immediate: true },
)

// 切换建筑图层显示/隐藏
const toggleBuilding = () => {
  if (!amap.value) return

  if (!showBuilding.value) {
    // 显示建筑
    showBuildingLayer()
  } else {
    // 隐藏建筑
    hideBuildingLayer()
  }

  // 发送状态变更事件
  emit("building-toggle", showBuilding.value)
}

// 显示建筑图层
const showBuildingLayer = () => {
  if (!amap.value) return

  if (mapFeatures.value.includes("building")) {
    // 如果地图已经包含建筑特性，则不需要重复设置
    return
  }

  mapFeatures.value.push("building")
  // 设置地图显示建筑物
  amap.value.setFeatures(mapFeatures.value)

  // 如果是3D模式，可以添加3D建筑效果
  if (amap.value.getViewMode() === "3D") {
    try {
      const buildingLayer = new window.AMap.Buildings({
        zIndex: 130,
        heightFactor: 1,
        wallColor: [200, 210, 220, 0.6], // 更柔和的灰白色墙体，半透明
        roofColor: "rgba(225, 235, 245, 0.7)", // 淡蓝灰色屋顶，半透明
        zooms: [16, 20],
      })
      amap.value.add(buildingLayer)
      // 将建筑层存储在组件实例中，以便后续移除
      buildingLayer3D.value = buildingLayer
    } catch (error) {
      console.error("添加3D建筑图层失败", error)
    }
  }
}

// 隐藏建筑图层
const hideBuildingLayer = () => {
  if (!amap.value) return

  if (!mapFeatures.value.includes("building")) {
    // 如果地图已经不包含建筑特性，则不需要重复设置
    return
  }

  mapFeatures.value = mapFeatures.value.filter((feature: string) => feature !== "building")
  // 设置地图不显示建筑物
  amap.value.setFeatures(mapFeatures.value)

  // 如果有3D建筑图层，也需要移除
  if (buildingLayer3D.value) {
    amap.value.remove(buildingLayer3D.value)
    buildingLayer3D.value = null
  }
}

// 用于存储3D建筑图层引用
const buildingLayer3D = ref<any | null>(null)

onMounted(() => {
  // 如果初始状态为显示，则自动显示建筑图层
  if (props.show) {
    showBuildingLayer()
  }
})

// 暴露方法
defineExpose({
  toggleBuilding,
  showBuildingLayer,
  hideBuildingLayer,
})
</script>

<style scoped>
.amap-building-layer-tool {
  display: contents;
}

.building-control {
  background-color: white;
  padding: 5px;
  border-radius: 4px;
  margin-bottom: 5px;
  filter: grayscale(1);
  img {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}

.building-active {
  filter: none;
}
</style>
